import type { PlayHandle, PlayOptions, SeslenInstance, SoundFactory, SoundSource, SourceDefaults } from 'seslen'
import type { PresetName } from 'seslen/presets'

const STORAGE_KEY = 'mm:sound-enabled'
const APP_GAIN = 1.25

type AppSoundName = PresetName | 'wobble'

const wobble: SoundFactory = (ctx, destination, opts) => {
  const t = ctx.currentTime
  const rate = opts.rate ?? 1
  const gain = opts.gain ?? 1
  const detune = opts.detune ?? 0

  const main = ctx.createOscillator()
  const wobbleLfo = ctx.createOscillator()
  const wobbleDepth = ctx.createGain()
  const output = ctx.createGain()

  main.type = 'sine'
  main.detune.value = detune
  main.frequency.setValueAtTime(1800 * rate, t)
  main.frequency.exponentialRampToValueAtTime(2350 * rate, t + 0.035)
  main.frequency.exponentialRampToValueAtTime(1650 * rate, t + 0.075)

  wobbleLfo.type = 'sine'
  wobbleLfo.frequency.value = 42
  wobbleDepth.gain.value = 65 * rate

  output.gain.setValueAtTime(0.0001, t)
  output.gain.linearRampToValueAtTime(0.04 * gain, t + 0.004)
  output.gain.exponentialRampToValueAtTime(0.0001, t + 0.08)

  wobbleLfo.connect(wobbleDepth).connect(main.frequency)
  main.connect(output).connect(destination)

  main.start(t)
  wobbleLfo.start(t)
  main.stop(t + 0.085)
  wobbleLfo.stop(t + 0.085)

  let done = false
  let ended: (() => void) | undefined
  let remaining = 2
  const finish = () => {
    remaining -= 1
    if (remaining > 0 || done)
      return
    done = true
    ended?.()
  }
  main.onended = finish
  wobbleLfo.onended = finish

  return {
    stop() {
      if (done)
        return
      done = true
      main.stop()
      wobbleLfo.stop()
      ended?.()
    },
    get done() {
      return done
    },
    duration: null,
    onEnded(cb: () => void) {
      ended = cb
    },
  } satisfies PlayHandle
}

let _sesPromise: Promise<SeslenInstance<AppSoundName> | null> | null = null
let audioUnlockListenersBound = false

function loadSeslen(): Promise<SeslenInstance<AppSoundName> | null> {
  if (!import.meta.client)
    return Promise.resolve(null)
  if (_sesPromise)
    return _sesPromise

  _sesPromise = Promise.all([
    import('seslen'),
    import('seslen/presets'),
  ]).then(([{ createSeslen }, { presetDefaults, presets }]) => {
    const appPresets = {
      ...presets,
      wobble,
    } satisfies Record<AppSoundName, SoundSource>

    const appPresetDefaults = {
      ...presetDefaults,
      wobble: {
        minInterval: 90,
        voices: 2,
        steal: 'oldest',
        rateJitter: 0.05,
        gainJitter: 0.08,
      },
    } satisfies Partial<Record<AppSoundName, SourceDefaults>>

    return createSeslen<AppSoundName>({
      sources: appPresets,
      defaults: appPresetDefaults,
      volume: 1,
      respectReducedMotion: false,
    })
  })

  return _sesPromise
}

function useBindAudioUnlock() {
  if (!import.meta.client || audioUnlockListenersBound)
    return

  audioUnlockListenersBound = true
  const unlocked = useState<boolean>('mm-sound-unlocked', () => false)

  if (navigator.userActivation?.hasBeenActive) {
    unlocked.value = true
    return
  }

  const unlock = () => {
    unlocked.value = true
    window.removeEventListener('pointerdown', unlock, true)
    window.removeEventListener('keydown', unlock, true)
    window.removeEventListener('touchstart', unlock, true)
  }

  window.addEventListener('pointerdown', unlock, true)
  window.addEventListener('keydown', unlock, true)
  window.addEventListener('touchstart', unlock, true)
}

export function useSound() {
  const enabled = useState<boolean>('mm-sound-enabled', () => false)
  const unlocked = useState<boolean>('mm-sound-unlocked', () => false)

  if (import.meta.client) {
    useBindAudioUnlock()
    if (getCurrentInstance())
      onMounted(() => { enabled.value = localStorage.getItem(STORAGE_KEY) === '1' })
    else
      enabled.value = localStorage.getItem(STORAGE_KEY) === '1'
  }

  function setEnabled(next: boolean) {
    enabled.value = next
    if (import.meta.client) {
      localStorage.setItem(STORAGE_KEY, next ? '1' : '0')
      if (next) {
        unlocked.value = true
        void loadSeslen()
      }
    }
  }

  function toggle() {
    setEnabled(!enabled.value)
  }

  function play(name: AppSoundName, opts?: PlayOptions) {
    if (!enabled.value || !unlocked.value || !import.meta.client)
      return
    void loadSeslen().then(ses => ses?.play(name, {
      ...opts,
      gain: (opts?.gain ?? 1) * APP_GAIN,
    }))
  }

  return { enabled, setEnabled, toggle, play }
}
